/**
 * Profile-scoped documents and extractions.
 *
 * Distinct from src/api/documents.ts (which is tied to a service request).
 * These documents have `customer_id` set and `service_request_id = null`.
 *
 * Endpoints (verified against /openapi.json):
 *   POST /api/v1/customer/profile/documents/initiate-upload         → get presigned S3 PUT URL
 *   POST /api/v1/customer/profile/documents/{doc_id}/confirm-upload → confirm S3 upload landed
 *   GET  /api/v1/customer/profile/documents              → list profile docs
 *   GET  /api/v1/customer/profile/document-types/supported  → which doc types have extractors
 *   GET  /api/v1/customer/profile/extracted              → aggregated extracted data
 *
 * The local types here are hand-rolled because `pnpm gen:types` hasn't been
 * regenerated against the new backend endpoints yet. When it is, replace them
 * with imports from `@finmin/api-client`.
 */
import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ApiError, createKeyFactory } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';

// ---------- types -----------------------------------------------------------

export type ProfileDocumentReviewStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'needs_reupload';

/**
 * Same shape as the SR-scoped DocumentResponse, but service_request_id is null
 * and customer_id is set. Kept local until the OpenAPI generator catches up.
 */
export interface ProfileDocumentResponse {
  id: string;
  service_request_id: string | null;
  customer_id: string | null;
  document_type: string;
  mime_type: string;
  uploaded_by_id: string;
  uploader_role: 'admin' | 'customer' | 'provider';
  document_label: string | null;
  review_status: ProfileDocumentReviewStatus;
  created_at: string;
  updated_at: string;
  is_downloadable: boolean;
}

export interface SupportedTypesResponse {
  document_types: string[];
}

// ---- Extracted profile (term + health insurance for now) ----

export type TermInsuranceVariant =
  | 'LEVEL'
  | 'INCREASING'
  | 'DECREASING'
  | 'WHOLE_LIFE'
  | 'RETURN_OF_PREMIUM'
  | 'OTHER';

export type TermPolicyStatus =
  | 'ACTIVE'
  | 'GRACE_PERIOD'
  | 'LAPSED'
  | 'SURRENDERED'
  | 'MATURED'
  | 'UNKNOWN';

export type PremiumFrequency =
  | 'ANNUAL'
  | 'SEMI_ANNUAL'
  | 'QUARTERLY'
  | 'MONTHLY'
  | 'SINGLE'
  | 'OTHER';

export type HealthCoverType =
  | 'INDIVIDUAL'
  | 'FAMILY_FLOATER'
  | 'GROUP'
  | 'SENIOR_CITIZEN'
  | 'TOP_UP'
  | 'SUPER_TOP_UP'
  | 'OTHER';

export interface TermInsuranceNominee {
  name: string | null;
  relationship: string | null;
  share_pct: number | null;
  age: number | null;
  is_minor: boolean | null;
  appointee_name: string | null;
}

export interface HealthInsuredMember {
  name: string | null;
  relationship: string | null;
  dob: string | null;
  age: number | null;
  gender: string | null;
  is_proposer: boolean | null;
}

export interface TermInsurancePolicyExtracted {
  id: string;
  customer_id: string;
  source_document_id: string;
  extractor_version: string;
  extracted_at: string;
  is_manually_edited: boolean;
  last_edited_by_admin_id: string | null;

  insurer_name: string | null;
  policy_number: string | null;
  claim_helpline: string | null;
  customer_care: string | null;
  claims_email: string | null;
  branch_address: string | null;
  irdai_reg_no: string | null;
  website: string | null;

  plan_name: string | null;
  plan_uin: string | null;

  sum_assured: string | null; // Decimal arrives as string over JSON
  variant: TermInsuranceVariant | null;
  variant_description: string | null;
  riders: string[];

  premium_amount: string | null;
  premium_frequency: PremiumFrequency | null;
  premium_year1_amount: string | null;
  premium_steady_state_amount: string | null;
  premium_gst_included: boolean | null;
  premium_notes: string | null;

  term_years: number | null;
  valid_till_age: number | null;
  risk_commencement_date: string | null;
  maturity_date: string | null;
  premium_paying_term: number | null;
  policy_status: TermPolicyStatus | null;
  next_premium_due_date: string | null;
  has_auto_debit: boolean | null;

  policyholder_name: string | null;
  policyholder_dob: string | null;
  life_assured_name: string | null;
  life_assured_dob: string | null;

  nominees: TermInsuranceNominee[];

  extraction_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface HealthInsurancePolicyExtracted {
  id: string;
  customer_id: string;
  source_document_id: string;
  extractor_version: string;
  extracted_at: string;
  is_manually_edited: boolean;
  last_edited_by_admin_id: string | null;

  insurer_name: string | null;
  policy_number: string | null;
  claim_helpline: string | null;
  customer_care: string | null;
  claims_email: string | null;
  branch_address: string | null;
  irdai_reg_no: string | null;
  website: string | null;
  tpa_name: string | null;
  tpa_phone: string | null;

  plan_name: string | null;
  plan_uin: string | null;

  sum_insured: string | null;
  cover_type: HealthCoverType | null;
  cover_type_description: string | null;
  cumulative_bonus_pct: string | null;
  restoration_benefit: boolean | null;
  room_rent_limit: string | null;
  icu_limit: string | null;
  copay_pct: string | null;
  deductible_inr: string | null;

  initial_waiting_period_days: number | null;
  pre_existing_disease_waiting_months: number | null;
  specific_disease_waiting_months: number | null;
  maternity_waiting_months: number | null;

  premium_amount: string | null;
  premium_frequency: PremiumFrequency | null;
  premium_gst_included: boolean | null;
  premium_notes: string | null;

  policy_period_start: string | null;
  policy_period_end: string | null;
  renewal_date: string | null;
  inception_date: string | null;

  proposer_name: string | null;
  proposer_dob: string | null;

  insured_members: HealthInsuredMember[];

  is_cashless_available: boolean | null;
  network_hospital_count: number | null;

  maternity_benefit: boolean | null;
  opd_benefit: boolean | null;
  critical_illness_addon: boolean | null;
  personal_accident_addon: boolean | null;
  hospital_cash_addon: boolean | null;
  ayush_coverage: boolean | null;
  day_care_covered: boolean | null;

  riders: string[];

  extraction_notes: string | null;
  created_at: string;
  updated_at: string;
}

// ---- NPS ----

export type NpsTier = 'TIER_I' | 'TIER_II' | 'BOTH' | 'UNKNOWN';
export type NpsAccountStatus = 'ACTIVE' | 'FROZEN' | 'CLOSED' | 'UNKNOWN';
export type NpsAssetClass =
  | 'EQUITY'
  | 'CORPORATE_BONDS'
  | 'GOVT_SECURITIES'
  | 'ALTERNATIVE'
  | 'OTHER';
export type NpsContributionType =
  | 'EMPLOYEE'
  | 'EMPLOYER'
  | 'VOLUNTARY'
  | 'OTHER';

export interface NpsNominee {
  name: string | null;
  relationship: string | null;
  share_pct: number | null;
  age: number | null;
  is_minor: boolean | null;
  guardian_name: string | null;
}

export interface NpsSchemeHolding {
  pfm_name: string | null;
  asset_class: NpsAssetClass | null;
  scheme_name: string | null;
  tier: NpsTier | null;
  nav: number | null;
  units: number | null;
  value: number | null;
}

export interface NpsContribution {
  contribution_date: string | null;
  amount: number | null;
  contribution_type: NpsContributionType | null;
  mode: string | null;
}

export interface NpsAccountExtracted {
  id: string;
  customer_id: string;
  source_document_id: string;
  extractor_version: string;
  extracted_at: string;
  is_manually_edited: boolean;
  last_edited_by_admin_id: string | null;

  pran: string | null;
  subscriber_name: string | null;
  account_status: NpsAccountStatus | null;
  tier: NpsTier | null;
  registration_date: string | null;

  portfolio_value: string | null;
  total_contributions: string | null;
  total_gain_loss: string | null;
  xirr_pct: string | null;

  employee_contribution_total: string | null;
  employer_contribution_total: string | null;

  equity_pct: string | null;
  corporate_bonds_pct: string | null;
  govt_securities_pct: string | null;
  alternative_pct: string | null;

  statement_from: string | null;
  statement_to: string | null;

  nominees: NpsNominee[];
  scheme_holdings: NpsSchemeHolding[];
  contributions: NpsContribution[];

  extraction_notes: string | null;
  created_at: string;
  updated_at: string;
}

// ---- Home loan ----

export type HomeLoanType =
  | 'HOME_LOAN'
  | 'HOME_IMPROVEMENT'
  | 'HOME_EXTENSION'
  | 'PLOT_LOAN'
  | 'TOPUP'
  | 'BALANCE_TRANSFER'
  | 'OTHER';

export type LoanStatus =
  | 'ACTIVE'
  | 'CLOSED'
  | 'IN_PROCESS'
  | 'OVERDUE'
  | 'PREPAID'
  | 'UNKNOWN';

export type InterestRateType = 'FLOATING' | 'FIXED' | 'HYBRID' | 'UNKNOWN';
export type LoanBenchmark = 'REPO' | 'MCLR' | 'BASE_RATE' | 'PLR' | 'OTHER';

export interface HomeLoanCoApplicant {
  name: string | null;
  relationship: string | null;
  pan: string | null;
}

export interface HomeLoanExtracted {
  id: string;
  customer_id: string;
  source_document_id: string;
  extractor_version: string;
  extracted_at: string;
  is_manually_edited: boolean;
  last_edited_by_admin_id: string | null;

  bank_name: string | null;
  branch_name: string | null;
  loan_account_number: string | null;

  loan_type: HomeLoanType | null;
  loan_status: LoanStatus | null;
  sanctioned_amount: string | null;
  disbursed_amount: string | null;
  outstanding_principal: string | null;

  tenure_months: number | null;
  remaining_tenure_months: number | null;
  sanction_date: string | null;
  first_emi_date: string | null;
  maturity_date: string | null;

  monthly_emi: string | null;

  interest_rate_type: InterestRateType | null;
  current_interest_rate_pct: string | null;
  benchmark: LoanBenchmark | null;
  benchmark_rate_pct: string | null;
  spread_pct: string | null;
  reset_frequency: string | null;

  processing_fee_text: string | null;
  administrative_fee_text: string | null;
  penal_interest_text: string | null;
  conversion_charges_text: string | null;
  part_prepayment_charges_text: string | null;
  foreclosure_charges_text: string | null;

  insurance_required: boolean | null;
  insurance_provider: string | null;

  has_co_applicants: boolean | null;
  co_applicants: HomeLoanCoApplicant[];

  branch_address: string | null;
  branch_phone: string | null;
  branch_manager_name: string | null;
  customer_care_phone: string | null;
  grievance_email: string | null;

  document_date: string | null;
  extraction_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ExtractedProfileResponse {
  term_insurance_policies: TermInsurancePolicyExtracted[];
  health_insurance_policies: HealthInsurancePolicyExtracted[];
  nps_accounts: NpsAccountExtracted[];
  home_loans: HomeLoanExtracted[];
}

// ---- Extraction runs (customer-triggered) ----

export type ExtractionStatus = 'pending' | 'running' | 'success' | 'failed';

export interface ExtractionRun {
  id: string;
  document_id: string;
  extractor_name: string;
  extractor_version: string;
  status: ExtractionStatus;
  triggered_by_admin_id: string | null;
  triggered_by_customer_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  preview: string | null;
  created_at: string;
  updated_at: string;
}

/** One profile document + its most-recent extraction run (slim, owner-scoped). */
export interface ProfileDocumentExtractionItem {
  document_id: string;
  document_type: string;
  document_label: string | null;
  mime_type: string;
  uploaded_at: string;
  latest_run_id: string | null;
  latest_run_status: ExtractionStatus | null;
  latest_run_started_at: string | null;
  latest_run_finished_at: string | null;
  latest_run_error: string | null;
  has_extractor: boolean;
}

export interface ProfileDocumentExtractionsResponse {
  items: ProfileDocumentExtractionItem[];
  total: number;
}

/** Renamed from MAX_UPLOAD_BYTES to avoid clashing with the SR-scoped
 * documents.ts export when both are re-exported from src/api/index.ts. */
export const MAX_PROFILE_UPLOAD_BYTES = 20 * 1024 * 1024;

// ---------- query keys ------------------------------------------------------

export const profileDocumentKeys = createKeyFactory('customer.profile.documents', {
  list: () => ['list'] as const,
  supported: () => ['supported-types'] as const,
  extracted: () => ['extracted'] as const,
  extractions: () => ['extractions'] as const,
});

// ---------- raw API functions -----------------------------------------------

export async function listProfileDocuments(signal?: AbortSignal) {
  const { data } = await apiClient.get<ProfileDocumentResponse[]>(
    '/api/v1/customer/profile/documents',
    { signal },
  );
  return data;
}

export async function getSupportedDocumentTypes(signal?: AbortSignal) {
  const { data } = await apiClient.get<SupportedTypesResponse>(
    '/api/v1/customer/profile/document-types/supported',
    { signal },
  );
  return data;
}

export async function getExtractedProfile(signal?: AbortSignal) {
  const { data } = await apiClient.get<ExtractedProfileResponse>(
    '/api/v1/customer/profile/extracted',
    { signal },
  );
  return data;
}

export async function listProfileDocumentExtractions(signal?: AbortSignal) {
  const { data } = await apiClient.get<ProfileDocumentExtractionsResponse>(
    '/api/v1/customer/profile/documents/extractions',
    { signal },
  );
  return data;
}

export async function triggerProfileExtraction(documentId: string) {
  const { data } = await apiClient.post<ExtractionRun>(
    `/api/v1/customer/profile/documents/${documentId}/extract`,
  );
  return data;
}

export async function deleteProfileDocument(documentId: string): Promise<void> {
  await apiClient.delete(
    `/api/v1/customer/profile/documents/${documentId}`,
  );
}

export async function uploadProfileDocument(
  file: File,
  documentType: string,
  options?: { documentLabel?: string; onProgress?: (percent: number) => void },
): Promise<ProfileDocumentResponse> {
  if (file.size > MAX_PROFILE_UPLOAD_BYTES) {
    throw new ApiError(
      `File exceeds 20 MB limit (got ${(file.size / 1024 / 1024).toFixed(1)} MB)`,
      413,
    );
  }

  const { data: initData } = await apiClient.post<{ document_id: string; upload_url: string; expires_in: number }>(
    '/api/v1/customer/profile/documents/initiate-upload',
    {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      file_size: file.size,
      document_type: documentType,
      document_label: options?.documentLabel,
    },
  );

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', initData.upload_url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && options?.onProgress) {
        options.onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new ApiError(`S3 upload failed (${xhr.status})`, xhr.status)));
    xhr.onerror = () => reject(new ApiError('Network error during upload', 0));
    xhr.send(file);
  });

  const { data } = await apiClient.post<ProfileDocumentResponse>(
    `/api/v1/customer/profile/documents/${initData.document_id}/confirm-upload`,
  );
  return data;
}

// ---------- hooks -----------------------------------------------------------

export function useProfileDocuments(
  options?: Omit<UseQueryOptions<ProfileDocumentResponse[], ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ProfileDocumentResponse[], ApiError>({
    queryKey: profileDocumentKeys.list(),
    queryFn: ({ signal }) => listProfileDocuments(signal),
    ...options,
  });
}

export function useSupportedProfileDocumentTypes(
  options?: Omit<UseQueryOptions<SupportedTypesResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<SupportedTypesResponse, ApiError>({
    queryKey: profileDocumentKeys.supported(),
    queryFn: ({ signal }) => getSupportedDocumentTypes(signal),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useExtractedProfile(
  options?: Omit<UseQueryOptions<ExtractedProfileResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ExtractedProfileResponse, ApiError>({
    queryKey: profileDocumentKeys.extracted(),
    queryFn: ({ signal }) => getExtractedProfile(signal),
    // Refetch periodically so an admin-triggered extraction shows up
    // without the user needing to refresh.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useProfileDocumentExtractions(
  options?: Omit<
    UseQueryOptions<ProfileDocumentExtractionsResponse, ApiError>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ProfileDocumentExtractionsResponse, ApiError>({
    queryKey: profileDocumentKeys.extractions(),
    queryFn: ({ signal }) => listProfileDocumentExtractions(signal),
    // Poll while anything is still in flight, then settle to no polling.
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      const inFlight = items.some(
        (it) =>
          it.latest_run_status === 'pending' ||
          it.latest_run_status === 'running',
      );
      return inFlight ? 5_000 : false;
    },
    refetchOnWindowFocus: true,
    ...options,
  });
}

export function useTriggerProfileExtraction(
  options?: UseMutationOptions<ExtractionRun, ApiError, { documentId: string }>,
) {
  // Pull onSuccess out so a caller's onSuccess composes with (rather than
  // clobbers) the cache invalidation below — see useDeleteProfileDocument.
  const { onSuccess, ...rest } = options ?? {};
  return useMutation<ExtractionRun, ApiError, { documentId: string }>({
    mutationFn: ({ documentId }) => triggerProfileExtraction(documentId),
    onSuccess: (...args) => {
      // Refresh the document list (so the row flips to "Processing…") and the
      // extracted data (so the matching tab fills in once the run succeeds).
      queryClient.invalidateQueries({ queryKey: profileDocumentKeys.extractions() });
      queryClient.invalidateQueries({ queryKey: profileDocumentKeys.extracted() });
      onSuccess?.(...args);
    },
    ...rest,
  });
}

export function useDeleteProfileDocument(
  options?: UseMutationOptions<void, ApiError, { documentId: string }>,
) {
  // Destructure onSuccess so it's NOT in `...rest`; otherwise a caller passing
  // its own onSuccess (e.g. a toast) would override this invalidation and the
  // deleted row would linger on screen until a manual refresh.
  const { onSuccess, ...rest } = options ?? {};
  return useMutation<void, ApiError, { documentId: string }>({
    mutationFn: ({ documentId }) => deleteProfileDocument(documentId),
    onSuccess: (...args) => {
      // Drop the row from the documents/extractions list and refresh the
      // extracted data (the deleted doc's records cascade away server-side).
      queryClient.invalidateQueries({ queryKey: profileDocumentKeys.extractions() });
      queryClient.invalidateQueries({ queryKey: profileDocumentKeys.list() });
      queryClient.invalidateQueries({ queryKey: profileDocumentKeys.extracted() });
      onSuccess?.(...args);
    },
    ...rest,
  });
}

export function useUploadProfileDocument(
  options?: UseMutationOptions<
    ProfileDocumentResponse,
    ApiError,
    { file: File; documentType: string; documentLabel?: string; onProgress?: (p: number) => void }
  >,
) {
  const { onSuccess, ...rest } = options ?? {};
  return useMutation<
    ProfileDocumentResponse,
    ApiError,
    { file: File; documentType: string; documentLabel?: string; onProgress?: (p: number) => void }
  >({
    mutationFn: ({ file, documentType, documentLabel, onProgress }) =>
      uploadProfileDocument(file, documentType, { documentLabel, onProgress }),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: profileDocumentKeys.list() });
      queryClient.invalidateQueries({ queryKey: profileDocumentKeys.extractions() });
      onSuccess?.(...args);
    },
    ...rest,
  });
}
